import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  TrashIcon,
  XMarkIcon,
  PencilIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon,
  ClockIcon,
  UserIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CheckIcon,
  ArrowPathIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';
import { commentAPI, usersAPI, ticketsAPI, referenceAPI } from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import ImagePreview from '../../components/image/ImagePreview';
import useIsMobile from '../../hooks/useIsMobile';
import SubTicketsTab from '../tickets/SubTicketsTab';

const TicketDetailModal = ({ ticket, isOpen, onClose, onUpdate, onTicketChange }) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('details');
  const [newComment, setNewComment] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [addingComment, setAddingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [updatingComment, setUpdatingComment] = useState(false);
  const { user } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const adminRoles = ['Admin', 'Manager', 'IT Müdürü', 'Sistem Yöneticisi', 'IT Teknisyeni', 'Pazarlama Müdürü', 'Bölüm Müdürü'];

  // Add Comment File States
  const [commentFiles, setCommentFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleCommentFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setCommentFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeCommentFile = (indexToRemove) => {
    setCommentFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // isAdmin kontrolünü daha robust yapalım
  const getRoleName = () => {
    if (!user?.role) return null;
    if (typeof user.role === 'string') return user.role;
    if (typeof user.role === 'object') return user.role.role_name || user.role.name;
    return null;
  };

  const roleName = getRoleName();
  const isAdmin = user && (adminRoles.includes(roleName) || user.is_staff || user.is_superuser);

  // Assignee states
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Atanan kişiyi değiştirme yetkisi - Tüm kullanıcılar için açık
  const canChangeAssignee = () => {
    return !!user; // Giriş yapmış her kullanıcı değiştirebilir
  };

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const [images, setImages] = useState([]);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Ticket değiştiğinde yorumları ve görselleri yükle
  useEffect(() => {
    if (isOpen && ticket) {
      loadComments();
      loadImages();
      setEditedTitle(ticket.title || '');
      setEditedDescription(ticket.description || '');
      // Assignee'yi state'e set et
      setSelectedAssigneeId(ticket.assigned_to_user?.id || ticket.assigned_to_user_id || null);
      // Ticket'ın projesine göre kullanıcıları yükle
      const projectId = ticket.project_id || (ticket.project?.id || (typeof ticket.project === 'number' ? ticket.project : null));
      if (projectId) {
        loadUsersForProject(projectId);
      }
    }
  }, [isOpen, ticket]);

  const loadActivity = useCallback(async () => {
    const id = ticket?.id;
    if (!id) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const res = await ticketsAPI.getTicketActivity(id);
      const list = Array.isArray(res?.data) ? res.data : [];
      setActivityItems([...list].reverse());
    } catch (e) {
      setActivityError(
        e?.message ||
          (language === 'tr' ? 'Geçmiş yüklenemedi.' : 'Could not load history.')
      );
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  }, [ticket?.id, language]);

  useEffect(() => {
    if (!isOpen || !ticket?.id) {
      return;
    }
    if (activeTab !== 'history') {
      return;
    }
    loadActivity();
  }, [isOpen, ticket?.id, activeTab, loadActivity]);

  const loadComments = async () => {
    if (!ticket?.id) return;

    setLoadingComments(true);
    try {
      const response = await commentAPI.getComments(ticket.id);

      // API response'u kontrol et - success field'ı olmayabilir
      if (response && (response.success === true || Array.isArray(response) || response.results)) {
        // DRF pagination format'ı kontrol et
        const commentsData = response.results || response.data || response;
        setComments(commentsData);
      }
    } catch (error) {
      // Silent error
    } finally {
      setLoadingComments(false);
    }
  };

  const loadImages = () => {
    if (!ticket?.attachments) return;

    // Attachment'lardan görselleri filtrele
    const imageAttachments = ticket.attachments.filter(attachment =>
      attachment.is_image || (attachment.file_type && attachment.file_type.startsWith('image/'))
    );

    // Görsel objelerini oluştur
    const imageObjects = imageAttachments.map(attachment => ({
      id: attachment.id,
      name: attachment.file_name,
      size: attachment.file_size,
      type: attachment.file_type,
      preview: attachment.file_url || `/api/tickets/${ticket.id}/serve-media/?path=media/${attachment.file_path}`,
      file_path: attachment.file_url || `/api/tickets/${ticket.id}/serve-media/?path=media/${attachment.file_path}`,
      optimized_url: attachment.optimized_url,
      thumbnail_url: attachment.thumbnail_url,
      description: attachment.description || '',
      tags: attachment.tags || [],
      uploadedAt: attachment.uploaded_at,
      width: attachment.width,
      height: attachment.height
    }));

    setImages(imageObjects);
  };

  // Proje bazında kullanıcıları yükle
  const loadUsersForProject = async (projectId) => {
    if (!projectId) return;

    try {
      setLoadingUsers(true);
      const response = await referenceAPI.getUsersByProjectDepartment(projectId);

      if (response?.success && response?.data) {
        setAvailableUsers(response.data || []);
      } else {
        // Fallback: Tüm kullanıcıları yükle
        const usersResponse = await usersAPI.getUsers();
        const usersList = usersResponse.results || usersResponse.data || (Array.isArray(usersResponse) ? usersResponse : []);
        setAvailableUsers(Array.isArray(usersList) ? usersList : []);
      }
    } catch (error) {
      // Silently handle error
      // Fallback: Tüm kullanıcıları yükle
      try {
        const usersResponse = await usersAPI.getUsers();
        const usersList = usersResponse.results || usersResponse.data || (Array.isArray(usersResponse) ? usersResponse : []);
        setAvailableUsers(Array.isArray(usersList) ? usersList : []);
      } catch (fallbackError) {
        // Silently handle error
        setAvailableUsers([]);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  // Reporter değişikliğini handle et (sadece state'i güncelle)
  const handleAssigneeChange = (e) => {
    const newAssigneeId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedAssigneeId(newAssigneeId);
  };

  // Save Changes butonu için handler
  const handleSaveChanges = async () => {
    if (!ticket?.id) return;

    const currentAssigneeId = ticket.assigned_to_user?.id || ticket.assigned_to_user_id;

    // Assignee değişikliği var mı kontrol et
    if (selectedAssigneeId !== currentAssigneeId) {
      try {
        setIsSaving(true);
        const response = await ticketsAPI.updateTicket(ticket.id, {
          assigned_to_user_id: selectedAssigneeId
        });

        if (response?.success || response?.id) {
          // Yeni assignee'yi bul
          const newAssignee = availableUsers.find(u => u.id === selectedAssigneeId);

          // Ticket'ı güncelle
          const updatedTicket = {
            ...ticket,
            assigned_to_user: newAssignee || null,
            assigned_to_user_id: selectedAssigneeId
          };
          onUpdate(updatedTicket);
          // Başarılı kayıt sonrası modal'ı kapat
          onClose();
        } else {
          alert(response?.message || 'Assignee güncellenirken hata oluştu');
          // State'i geri al
          setSelectedAssigneeId(currentAssigneeId);
        }
      } catch (error) {
        // Silently handle error
        alert('Assignee güncellenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        // State'i geri al
        setSelectedAssigneeId(currentAssigneeId);
      } finally {
        setIsSaving(false);
      }
    } else {
      // Değişiklik yoksa direkt kapat
      onClose();
    }
  };

  // Başlık düzenleme fonksiyonları
  const handleStartEditingTitle = () => {
    setEditedTitle(ticket.title || '');
    setIsEditingTitle(true);
  };

  const handleCancelEditingTitle = () => {
    setEditedTitle(ticket.title || '');
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    const trimmedTitle = editedTitle.trim();

    if (!trimmedTitle) {
      alert('Başlık boş olamaz!');
      return;
    }

    if (trimmedTitle === ticket.title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      setSavingTitle(true);
      const response = await ticketsAPI.updateTicket(ticket.id, {
        title: trimmedTitle
      });

      // Backend response formatını kontrol et
      // Response { success: true, data: {...} } veya { success: true, id: ..., title: ... } formatında olabilir
      if (response?.success) {
        // Response'dan güncellenmiş ticket bilgilerini al
        const updatedTicketData = response.data || response;

        const updatedTicket = {
          ...ticket,
          ...updatedTicketData,
          title: trimmedTitle // Kesinlikle yeni başlığı kullan
        };

        // onUpdate callback'ini çağır
        if (onUpdate) {
          onUpdate(updatedTicket);
        }

        setIsEditingTitle(false);
      } else if (response?.id) {
        // Eğer success field yoksa ama id varsa (eski format)
        const updatedTicket = {
          ...ticket,
          ...response,
          title: trimmedTitle
        };

        if (onUpdate) {
          onUpdate(updatedTicket);
        }

        setIsEditingTitle(false);
      } else {
        // Silently handle error
        alert(response?.message || 'Başlık güncellenirken hata oluştu');
        setEditedTitle(ticket.title || '');
      }
    } catch (error) {
      // Silently handle error
      alert('Başlık güncellenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      setEditedTitle(ticket.title || '');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditingTitle();
    }
  };

  // Açıklama düzenleme fonksiyonları
  const handleStartEditingDescription = () => {
    setEditedDescription(ticket.description || '');
    setIsEditingDescription(true);
  };

  const handleCancelEditingDescription = () => {
    setEditedDescription(ticket.description || '');
    setIsEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    const trimmedDescription = editedDescription.trim();

    if (trimmedDescription === ticket.description) {
      setIsEditingDescription(false);
      return;
    }

    try {
      setSavingDescription(true);
      const response = await ticketsAPI.updateTicket(ticket.id, {
        description: trimmedDescription
      });

      if (response?.success) {
        const updatedTicketData = response.data || response;
        const updatedTicket = {
          ...ticket,
          ...updatedTicketData,
          description: trimmedDescription
        };

        if (onUpdate) {
          onUpdate(updatedTicket);
        }
        setIsEditingDescription(false);
      } else if (response?.id) {
        const updatedTicket = {
          ...ticket,
          ...response,
          description: trimmedDescription
        };
        if (onUpdate) {
          onUpdate(updatedTicket);
        }
        setIsEditingDescription(false);
      } else {
        alert(response?.message || 'Açıklama güncellenirken hata oluştu');
      }
    } catch (error) {
      alert('Açıklama güncellenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setSavingDescription(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && commentFiles.length === 0) return;
    if (!ticket?.id) return;

    setAddingComment(true);
    try {
      let commentAdded = false;
      let newCommentId = null;

      if (newComment.trim()) {
        const response = await commentAPI.addComment(ticket.id, newComment.trim());

        if (response && (response.success === true || response.id)) {
          setComments(prevComments => [response, ...prevComments]);
          setNewComment('');
          commentAdded = true;
          newCommentId = response.id;
        } else {
          alert('Yorum eklenirken hata oluştu: ' + (response?.message || 'Bilinmeyen hata'));
        }
      }

      if (commentFiles.length > 0) {
        const formData = new FormData();
        commentFiles.forEach(file => {
          formData.append('files', file);
        });

        // Eğer yorum atıldıysa, dönen yorumun ID'sini de gönder
        if (commentAdded && newCommentId) {
          formData.append('comment_id', newCommentId);
        }

        const fileResponse = await ticketsAPI.uploadAttachments(ticket.id, formData);

        if (fileResponse && fileResponse.success) {
          setCommentFiles([]);
          loadImages(); // Update images/attachments if necessary
          if (!newComment.trim() && !commentAdded) {
            // Sadece dosya yüklendiğinde bir işleme gerekirse
          }
        } else {
          alert('Dosyalar yüklenirken hata oluştu: ' + (fileResponse?.message || 'Bilinmeyen hata'));
        }
      }

      await loadComments();
    } catch (error) {
      alert('İşlem sırasında hata oluştu: ' + error.message);
    } finally {
      setAddingComment(false);
    }
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditedCommentText(comment.comment_text);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleUpdateComment = async (commentId) => {
    if (!editedCommentText.trim() || !ticket?.id) return;

    setUpdatingComment(true);
    try {
      const response = await commentAPI.updateComment(ticket.id, commentId, editedCommentText.trim());

      if (response && (response.success === true || response.id)) {
        // Yorum listesini güncelle
        setComments(prevComments =>
          prevComments.map(c => c.id === commentId ? { ...c, comment_text: editedCommentText.trim() } : c)
        );
        setEditingCommentId(null);
        setEditedCommentText('');
      } else {
        alert('Yorum güncellenirken hata oluştu');
      }
    } catch (error) {
      alert('Yorum güncellenirken hata oluştu');
    } finally {
      setUpdatingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!ticket?.id || !window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await commentAPI.deleteComment(ticket.id, commentId);

      // Delete genelde boş döner veya {success: true} döner
      setComments(prevComments => prevComments.filter(c => c.id !== commentId));
    } catch (error) {
      alert('Yorum silinirken hata oluştu');
    }
  };



  const handleImagePreview = (image) => {
    let imageIndex = images.findIndex(img => img.id === image.id);

    if (imageIndex === -1) {
      const newImage = {
        id: image.id,
        name: image.file_name || image.name,
        size: image.file_size || image.size,
        type: image.file_type || image.type,
        preview: image.thumbnail_url || image.file_url || image.preview || `/api/tickets/public-media/${ticket.id}?path=media/${image.file_path}`,
        file_path: image.file_url || image.file_path || `/api/tickets/public-media/${ticket.id}?path=media/${image.file_path}`,
        optimized_url: image.optimized_url,
        thumbnail_url: image.thumbnail_url,
      };
      setImages(prev => [...prev, newImage]);
      setCurrentImageIndex(images.length);
    } else {
      setCurrentImageIndex(imageIndex);
    }
    setShowImagePreview(true);
  };

  const handleImageNavigate = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  if (!isOpen || !ticket) return null;

  const getTypeIcon = (type) => {
    // Type undefined, null veya string değilse default icon döndür
    if (!type || typeof type !== 'string') {
      return '📄';
    }

    switch (type) {
      case 'Bug': return '🐛';
      case 'Story': return '📖';
      case 'Task': return '✅';
      case 'Epic': return '⚡';
      default: return '📄';
    }
  };

  const getStatusColor = (status) => {
    let statusText = status;
    if (status && typeof status === 'object' && status.status_name) {
      statusText = status.status_name;
    }

    if (!statusText || typeof statusText !== 'string') {
      return 'bg-background-secondary text-text-secondary border-background-secondary dark:bg-dark-background-secondary/20 dark:text-dark-text-light dark:border-dark-border';
    }

    switch (statusText.toLowerCase()) {
      case 'to do':
      case 'beklemede':
        return 'bg-background-secondary text-text-primary border-background-secondary dark:bg-dark-background-secondary dark:text-dark-text-primary dark:border-dark-border';
      case 'in progress':
      case 'yapılıyor':
        return 'bg-highlight-50 text-highlight-600 border-highlight-200 dark:bg-highlight-500/10 dark:text-highlight-500 dark:border-highlight-500/20';
      case 'done':
      case 'bitti':
        return 'bg-success-50 text-success-600 border-success-200 dark:bg-success-500/10 dark:text-success-500 dark:border-success-500/20';
      default: return 'bg-background-secondary text-text-secondary border-background-secondary dark:bg-dark-background-secondary/20';
    }
  };

  const getPriorityColor = (priority) => {
    let priorityText = priority;
    if (priority && typeof priority === 'object' && priority.priority_name) {
      priorityText = priority.priority_name;
    }

    if (!priorityText || typeof priorityText !== 'string') {
      return 'text-text-secondary bg-background-secondary border-background-secondary dark:text-dark-text-light dark:bg-dark-background-secondary/20 dark:border-dark-border';
    }

    switch (priorityText.toLowerCase()) {
      case 'critical': return 'text-error-600 bg-error-50 border-error-100 dark:text-error-500 dark:bg-error-500/10 dark:border-error-500/20';
      case 'high': return 'text-warning-600 bg-warning-50 border-warning-100 dark:text-warning-500 dark:bg-warning-500/10 dark:border-warning-500/20';
      case 'medium': return 'text-highlight-600 bg-highlight-50 border-highlight-100 dark:text-highlight-500 dark:bg-highlight-500/10 dark:border-highlight-500/20';
      case 'low': return 'text-success-600 bg-success-50 border-success-100 dark:text-success-500 dark:bg-success-500/10 dark:border-success-500/20';
      default: return 'text-text-secondary bg-background-secondary border-background-secondary dark:text-dark-text-light dark:bg-dark-background-secondary/20 dark:border-dark-border';
    }
  };

  const parseTicketResponse = (res) => {
    if (!res) return null;
    if (res.success && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
      return res.data;
    }
    const { success, message, timestamp, ...rest } = res;
    return rest.id ? rest : null;
  };

  const handleBackToParent = async () => {
    if (!ticket?.parent_ticket?.id) return;
    try {
      const res = await ticketsAPI.getTicket(ticket.parent_ticket.id);
      const parsed = parseTicketResponse(res);
      if (parsed && onTicketChange) {
        onTicketChange(parsed);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenChildFromSubTab = async (subRow) => {
    if (!subRow?.id) return;
    try {
      const res = await ticketsAPI.getTicket(subRow.id);
      const parsed = parseTicketResponse(res);
      if (parsed && onTicketChange) {
        onTicketChange(parsed);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRefreshParentTicket = (full) => {
    if (full && onUpdate) {
      onUpdate(full);
    }
  };

  const projectIdForSubs = ticket?.project_id ?? ticket?.project?.id;

  const actorLabel = (actor) => {
    if (!actor) return t('unknownUser');
    const n = `${actor.first_name || ''} ${actor.last_name || ''}`.trim();
    return n || actor.email || t('unknownUser');
  };

  const handleOpenChildFromActivity = async (childId) => {
    if (!childId || !onTicketChange) return;
    try {
      const res = await ticketsAPI.getTicket(childId);
      const parsed = parseTicketResponse(res);
      if (parsed) onTicketChange(parsed);
    } catch (e) {
      console.error(e);
    }
  };

  const renderFieldChangedRow = (name, p) => {
    const f = p.field;
    if (f === 'priority') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('updatedStatusFrom')}{' '}
          <span className="font-medium">{p.old_name || '—'}</span> {t('to')}{' '}
          <span className="font-medium">{p.new_name || '—'}</span>
          <span className="text-xs text-text-secondary dark:text-dark-text-secondary ml-1">
            ({t('historyFieldPriority')})
          </span>
        </p>
      );
    }
    if (f === 'assigned_to_user') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('updatedStatusFrom')}{' '}
          <span className="font-medium">{p.old_name || t('unassigned')}</span> {t('to')}{' '}
          <span className="font-medium">{p.new_name || t('unassigned')}</span>
          <span className="text-xs text-text-secondary dark:text-dark-text-secondary ml-1">
            ({t('historyFieldAssignee')})
          </span>
        </p>
      );
    }
    if (f === 'title') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyFieldTitle')}:{' '}
          <span className="line-through opacity-70 break-words">{p.old || ''}</span> →{' '}
          <span className="break-words">{p.new || ''}</span>
        </p>
      );
    }
    if (f === 'description') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyDescriptionUpdated')}
        </p>
      );
    }
    if (f === 'due_date') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyFieldDueDate')}: {p.old || '—'} → {p.new || '—'}
        </p>
      );
    }
    if (f === 'topic') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyFieldTopic')}: {p.old_name || '—'} → {p.new_name || '—'}
        </p>
      );
    }
    if (f === 'ticket_type') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyFieldTicketType')}: {p.old_name || '—'} → {p.new_name || '—'}
        </p>
      );
    }
    if (f === 'parent_ticket') {
      return (
        <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
          <span className="font-bold">{name}</span> {t('historyFieldParentTicket')}: {p.old_title || '—'} → {p.new_title || '—'}
        </p>
      );
    }
    return (
      <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
        <span className="font-bold">{name}</span>{' '}
        <span className="text-xs text-text-secondary">{f || 'field'}</span>
      </p>
    );
  };

  const renderHistoryEntry = (item) => {
    const p = item.payload || {};
    const name = actorLabel(item.actor);
    const timeStr = item.created_at
      ? new Date(item.created_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')
      : '';

    const iconWrap = (Icon, colorClass) => (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-5 h-5" />
      </div>
    );

    let body = null;
    let icon = iconWrap(UserIcon, 'bg-primary-500/10 text-primary-600 dark:text-primary-400');

    switch (item.event_type) {
      case 'ticket_created':
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
            <span className="font-bold">{name}</span> {t('createdThisTicket')}
          </p>
        );
        icon = iconWrap(CheckCircleIcon, 'bg-success-500/10 text-success-600 dark:text-success-400');
        break;
      case 'status_changed':
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
            <span className="font-bold">{name}</span> {t('updatedStatusFrom')}{' '}
            <span className="mx-1 px-2 py-0.5 bg-background-main dark:bg-dark-background-main rounded text-xs font-bold text-text-secondary uppercase">
              {p.old_status_name || '—'}
            </span>{' '}
            {t('to')}{' '}
            <span className="mx-1 px-2 py-0.5 bg-highlight-500/10 text-highlight-600 dark:text-highlight-500 rounded text-xs font-bold uppercase">
              {p.new_status_name || '—'}
            </span>
          </p>
        );
        icon = iconWrap(ArrowPathIcon, 'bg-primary-500/10 text-primary-600 dark:text-primary-400');
        break;
      case 'sub_ticket_added':
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
            <span className="font-bold">{name}</span> {t('historySubTicketAdded')}{' '}
            {onTicketChange && p.child_ticket_id ? (
              <button
                type="button"
                onClick={() => handleOpenChildFromActivity(p.child_ticket_id)}
                className="font-semibold text-highlight-600 dark:text-highlight-400 hover:underline"
                title={t('historyOpenSubTicket')}
              >
                {p.child_title || `#${p.child_ticket_id}`}
              </button>
            ) : (
              <span className="font-semibold">{p.child_title || `#${p.child_ticket_id}`}</span>
            )}
          </p>
        );
        icon = iconWrap(QueueListIcon, 'bg-highlight-500/10 text-highlight-600 dark:text-highlight-400');
        break;
      case 'comment_added':
        body = (
          <div>
            <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
              <span className="font-bold">{name}</span> {t('historyCommentAdded')}
              {p.preview ? (
                <span className="block mt-1 text-xs text-text-secondary dark:text-dark-text-secondary line-clamp-2">
                  {p.preview}
                </span>
              ) : null}
            </p>
          </div>
        );
        icon = iconWrap(ChatBubbleLeftIcon, 'bg-success-500/10 text-success-600 dark:text-success-400');
        break;
      case 'attachment_added':
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
            <span className="font-bold">{name}</span> {t('historyAttachmentAdded')}{' '}
            <span className="font-medium break-all">{p.file_name || ''}</span>
          </p>
        );
        icon = iconWrap(PaperClipIcon, 'bg-highlight-500/10 text-highlight-600 dark:text-highlight-400');
        break;
      case 'ticket_deactivated':
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary leading-relaxed">
            <span className="font-bold">{name}</span> {t('historyTicketDeactivated')}
          </p>
        );
        icon = iconWrap(TrashIcon, 'bg-error-500/10 text-error-600 dark:text-error-400');
        break;
      case 'field_changed':
        body = renderFieldChangedRow(name, p);
        icon = iconWrap(PencilIcon, 'bg-primary-500/10 text-primary-600 dark:text-primary-400');
        break;
      default:
        body = (
          <p className="text-sm text-text-primary dark:text-dark-text-primary">
            <span className="font-bold">{name}</span> {item.event_type}
          </p>
        );
    }

    return (
      <div
        key={item.id}
        className="flex items-start space-x-3 p-4 bg-background-secondary dark:bg-dark-background-secondary rounded-xl border border-background-secondary dark:border-dark-border shadow-sm"
      >
        {icon}
        <div className="flex-1 min-w-0">
          {body}
          <p className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary mt-1 uppercase tracking-widest">
            {timeStr}
          </p>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-[9999] outline-none focus:outline-none overflow-y-auto ${isMobile ? 'flex items-end justify-center p-0' : 'flex items-center justify-center p-4'}`}>
      <div className={`bg-white dark:bg-dark-background-main overflow-hidden flex flex-col transform transition-all animate-in fade-in zoom-in duration-300 shadow-2xl border border-background-secondary dark:border-dark-border ${isMobile ? 'rounded-t-2xl w-full max-h-[92dvh]' : 'rounded-2xl max-w-6xl w-full max-h-[90vh]'}`}>
        {/* Header */}
        <div className="border-b border-background-secondary dark:border-dark-border p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <span className="text-2xl">{getTypeIcon(ticket.type)}</span>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  {isEditingTitle ? (
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          onKeyDown={handleTitleKeyDown}
                          disabled={savingTitle}
                          className="w-full font-dynamic-title font-semibold text-text-primary dark:text-dark-text-primary bg-transparent border-b-2 border-primary-500 dark:border-primary-400 px-1 py-1.5 focus:outline-none focus:border-primary-600 dark:focus:border-primary-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          autoFocus
                          placeholder={t('editTitle') || "Başlık giriniz..."}
                        />
                        {savingTitle && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handleSaveTitle}
                          disabled={savingTitle || !editedTitle.trim()}
                          className="p-1.5 text-success-600 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300 hover:bg-success-50 dark:hover:bg-success-900/20 rounded-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                          title={t('save') || "Kaydet (Enter)"}
                        >
                          {savingTitle ? (
                            <div className="w-3.5 h-3.5 border-2 border-success-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <CheckIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={handleCancelEditingTitle}
                          disabled={savingTitle}
                          className="p-1.5 text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary hover:bg-background-secondary dark:hover:bg-dark-background-secondary rounded-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                          title={t('cancel') || "İptal (Esc)"}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-1">
                      <h2 className="font-dynamic-title font-semibold text-text-primary dark:text-dark-text-primary flex-1">{ticket.title}</h2>
                      <button
                        onClick={handleStartEditingTitle}
                        className="p-1 text-text-secondary hover:text-primary-600 dark:text-dark-text-secondary dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 rounded transition-colors duration-200"
                        title={t('editTitle')}
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-text-secondary dark:text-dark-text-light font-bold tracking-wider">#{ticket.id}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(ticket.status)}`}>
                    {ticket.status?.status_name || ticket.status}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority?.priority_name || ticket.priority}
                  </span>
                </div>
                {ticket.parent_ticket && (
                  <button
                    type="button"
                    onClick={handleBackToParent}
                    className="mt-2 text-left text-xs font-medium text-highlight-600 hover:text-highlight-700 dark:text-highlight-400 dark:hover:text-highlight-300"
                  >
                    ← {t('parentTicketLink')} #{ticket.parent_ticket.id}: {ticket.parent_ticket.title}
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-background-secondary dark:border-dark-border bg-background-main/30 dark:bg-dark-background-alt/30">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'details'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary'
                }`}
            >
              {t('details')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary'
                }`}
            >
              {t('history')}
            </button>
            <button
              onClick={() => setActiveTab('subtickets')}
              className={`py-4 px-1 border-b-2 font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'subtickets'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary'
                }`}
            >
              {t('subTickets')}
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'details' && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-xs uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary">{t('description')}</h3>
                      {!isEditingDescription && (
                        <button
                          onClick={handleStartEditingDescription}
                          className="p-1 text-text-secondary hover:text-primary-600 dark:text-dark-text-secondary dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 rounded transition-colors duration-200"
                          title={t('editDescription')}
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {isEditingDescription ? (
                      <div className="space-y-3">
                        <textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          disabled={savingDescription}
                          rows={6}
                          className="w-full font-dynamic-text text-text-primary dark:text-dark-text-primary bg-white dark:bg-dark-background-secondary border border-primary-500 dark:border-primary-400 rounded-lg p-4 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-all duration-200"
                          placeholder={t('editDescription') + "..."}
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEditingDescription}
                            disabled={savingDescription}
                            className="px-3 py-1.5 text-sm font-bold text-text-secondary dark:text-dark-text-secondary hover:bg-background-secondary dark:hover:bg-dark-background-secondary rounded-md transition-all whitespace-nowrap flex items-center gap-1"
                          >
                            <XMarkIcon className="w-4 h-4" />
                            {t('cancel')}
                          </button>
                          <button
                            onClick={handleSaveDescription}
                            disabled={savingDescription}
                            className="px-4 py-1.5 text-sm font-bold bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-all whitespace-nowrap flex items-center gap-1 shadow-sm"
                          >
                            {savingDescription ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <CheckIcon className="w-4 h-4" />
                            )}
                            {t('save')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-background-secondary dark:bg-dark-background-secondary rounded-lg p-4 group relative border border-background-secondary dark:border-dark-border">
                        <p className="font-dynamic-text text-text-primary dark:text-dark-text-primary whitespace-pre-wrap leading-relaxed">
                          {ticket.description || t('noDescription') || 'No description provided.'}
                        </p>
                      </div>
                    )}
                  </div>





                  {/* Comments */}
                  <div>
                    <h3 className="font-bold text-xs uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-3">{t('comments')}</h3>
                    <div className="space-y-4">
                      {loadingComments ? (
                        <div className="text-center py-6">
                          <p className="text-sm text-text-secondary dark:text-dark-text-secondary">{t('loading')}</p>
                        </div>
                      ) : comments.length > 0 ? (
                        <>
                          {/* Comments List */}
                          <div className="space-y-4">
                            {comments.map(comment => (
                              <div key={comment.id} className="flex space-x-3">
                                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-bold ring-2 ring-white dark:ring-dark-background-main">
                                  {comment.user?.first_name?.charAt(0) || comment.user?.email?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1">
                                  <div className="bg-background-secondary dark:bg-dark-background-secondary border border-background-secondary dark:border-dark-border rounded-xl p-3 shadow-sm">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-sm font-bold text-text-primary dark:text-dark-text-primary">
                                        {comment.user?.first_name && comment.user?.last_name
                                          ? `${comment.user.first_name} ${comment.user.last_name}`
                                          : comment.user?.email || (t('anonymous') || 'Anonim')
                                        }
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        <span className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                                          {comment.created_at ? new Date(comment.created_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US') : 'N/A'}
                                        </span>
                                        {(comment.user?.id === user?.id || isAdmin) && (
                                          <div className="flex items-center space-x-1 border-l border-background-main dark:border-dark-background-main pl-2">
                                            <button
                                              onClick={() => handleEditComment(comment)}
                                              className="p-1 text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
                                              title={t('edit')}
                                            >
                                              <PencilIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteComment(comment.id)}
                                              className="p-1 text-text-secondary hover:text-error-600 dark:hover:text-error-400 rounded transition-colors"
                                              title={t('delete')}
                                            >
                                              <TrashIcon className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {editingCommentId === comment.id ? (
                                      <div className="space-y-2 mt-2">
                                        <textarea
                                          value={editedCommentText}
                                          onChange={(e) => setEditedCommentText(e.target.value)}
                                          rows={2}
                                          className="w-full border border-primary-500 dark:border-primary-400 bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-600"
                                        />
                                        <div className="flex justify-end space-x-2">
                                          <button
                                            onClick={handleCancelEditComment}
                                            className="px-3 py-1 text-xs font-bold text-text-secondary dark:text-dark-text-secondary hover:bg-background-main dark:hover:bg-dark-background-main rounded-md transition-all"
                                          >
                                            {t('cancel')}
                                          </button>
                                          <button
                                            onClick={() => handleUpdateComment(comment.id)}
                                            disabled={updatingComment || !editedCommentText.trim()}
                                            className="px-3 py-1 text-xs font-bold bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 transition-all"
                                          >
                                            {updatingComment ? (t('updating') || 'Updating...') : (t('update') || 'Update')}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {comment.comment_text && (
                                          <p className="text-sm text-text-primary dark:text-dark-text-primary whitespace-pre-wrap leading-relaxed mb-2">{comment.comment_text}</p>
                                        )}
                                        {comment.attachments && comment.attachments.length > 0 && (
                                          <div className="flex flex-wrap gap-2 mt-2">
                                            {comment.attachments.map(att => (
                                              <div key={att.id} className="relative group">
                                                {att.file_type?.startsWith('image/') ? (
                                                  <div
                                                    className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer border border-background-secondary dark:border-dark-border"
                                                    onClick={() => {
                                                      handleImagePreview(att);
                                                    }}
                                                    title={att.file_name}
                                                  >
                                                    <img
                                                      src={att.thumbnail_url || att.file_url || `${window.location.origin}/api/tickets/public-media/${ticket.id}?path=media/${att.file_path}`}
                                                      alt={att.file_name}
                                                      className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                                                      onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                      }}
                                                    />
                                                    <div className="hidden w-full h-full bg-background-main dark:bg-dark-background-main flex items-center justify-center">
                                                      <span className="text-xs text-text-secondary text-center p-1 break-words line-clamp-2">{att.file_name}</span>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <a
                                                    href={(att.file_url || `${window.location.origin}/api/tickets/public-media/${ticket.id}?path=media/${att.file_path}`) + `&filename=${encodeURIComponent(att.file_name)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download={att.file_name}
                                                    className="flex items-center gap-3 px-3 py-2 bg-background-main dark:bg-dark-background-main rounded-lg border border-background-secondary dark:border-dark-border hover:bg-background-secondary dark:hover:bg-dark-background-secondary transition-colors"
                                                    title={att.file_name}
                                                  >
                                                    <div className="flex items-center justify-center w-8 h-8 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-bold text-[10px] uppercase shrink-0">
                                                      {att.file_name?.includes('.') ? att.file_name.split('.').pop().substring(0, 4) : 'FILE'}
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                      <span className="text-xs font-medium text-text-primary dark:text-dark-text-primary max-w-[180px] truncate">
                                                        {att.file_name}
                                                      </span>
                                                    </div>
                                                  </a>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Add Comment Form */}
                          <div className="bg-background-secondary dark:bg-dark-background-secondary/50 rounded-xl p-4 border border-background-secondary dark:border-dark-border mt-6">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder={t('addCommentPlaceholder') || "Add a comment..."}
                              rows={3}
                              className="w-full border border-background-secondary dark:border-dark-border bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder:text-text-secondary/50"
                            />
                            {commentFiles.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {commentFiles.map((file, index) => (
                                  <div key={index} className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 px-3 py-1.5 rounded-lg text-xs font-bold border border-primary-200 dark:border-primary-800">
                                    <span className="truncate max-w-[150px]">{file.name}</span>
                                    <button onClick={() => removeCommentFile(index)} className="hover:text-error-500">
                                      <XMarkIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex justify-between items-center mt-3">
                              <div className="flex gap-2">
                                <input
                                  type="file"
                                  multiple
                                  ref={fileInputRef}
                                  onChange={handleCommentFileChange}
                                  className="hidden"
                                />
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  className="p-2 text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-background-main dark:hover:bg-dark-background-main transition-colors"
                                  title={t('attachFiles') || 'Dosya Ekle'}
                                >
                                  <PaperClipIcon className="w-5 h-5" />
                                </button>
                              </div>
                              <button
                                onClick={handleAddComment}
                                disabled={(!newComment.trim() && commentFiles.length === 0) || addingComment}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-bold uppercase tracking-widest transition-all shadow-md shadow-primary-500/20"
                              >
                                {addingComment ? (t('adding') || 'Adding...') : t('addComment')}
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-10 bg-background-secondary/30 dark:bg-dark-background-secondary/10 rounded-2xl border-2 border-dashed border-background-secondary dark:border-dark-border">
                          <ChatBubbleLeftIcon className="w-12 h-12 text-text-secondary dark:text-dark-text-secondary mx-auto mb-3 opacity-20" />
                          <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-6 font-medium">{t('noComments') || 'No comments yet'}</p>
                          <div className="max-w-md mx-auto px-4">
                            <div className="bg-white dark:bg-dark-background-secondary rounded-xl p-4 border border-background-secondary dark:border-dark-border shadow-sm">
                              <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder={t('firstCommentPlaceholder') || "Be the first to comment..."}
                                rows={3}
                                className="w-full border-none bg-transparent text-text-primary dark:text-dark-text-primary rounded-md px-3 py-2 focus:outline-none focus:ring-0 placeholder:text-text-secondary/50"
                              />
                              {commentFiles.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2 px-3 pb-2">
                                  {commentFiles.map((file, index) => (
                                    <div key={index} className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 px-2 py-1 rounded-md text-xs font-bold border border-primary-200 dark:border-primary-800">
                                      <span className="truncate max-w-[120px]">{file.name}</span>
                                      <button onClick={() => removeCommentFile(index)} className="hover:text-error-500">
                                        <XMarkIcon className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex justify-between items-center mt-2 pt-2 border-t border-background-secondary dark:border-dark-border">
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  className="p-1.5 ml-2 text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-background-main dark:hover:bg-dark-background-main transition-colors"
                                  title={t('attachFiles') || 'Dosya Ekle'}
                                >
                                  <PaperClipIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleAddComment}
                                  disabled={(!newComment.trim() && commentFiles.length === 0) || addingComment}
                                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-bold uppercase tracking-widest transition-all"
                                >
                                  {addingComment ? (t('adding') || 'Adding...') : t('addComment')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-8">
                  {/* Assignee */}
                  <div>
                    <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-3">{t('assignee')}</h4>
                    {ticket.assigned_to_user ? (
                      <div className="flex items-center space-x-3 mb-4 p-2 bg-background-secondary dark:bg-dark-background-secondary rounded-lg border border-background-secondary dark:border-dark-border">
                        <div className="w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {ticket.assigned_to_user.first_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-text-primary dark:text-dark-text-primary truncate">
                            {ticket.assigned_to_user.full_name ||
                              `${ticket.assigned_to_user.first_name || ''} ${ticket.assigned_to_user.last_name || ''}`.trim() ||
                              t('unknownUser') || 'Unknown User'}
                          </p>
                          <p className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary truncate uppercase tracking-wider">
                            {ticket.assigned_to_user.email || 'No email'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4 p-3 bg-background-secondary/50 dark:bg-dark-background-secondary/20 rounded-lg border border-dashed border-background-secondary dark:border-dark-border">
                        <p className="text-xs text-text-secondary dark:text-dark-text-secondary italic font-medium">{t('unassigned') || 'Görev atanmamış'}</p>
                      </div>
                    )}
                    {canChangeAssignee() ? (
                      <select
                        value={selectedAssigneeId || ''}
                        onChange={handleAssigneeChange}
                        disabled={loadingUsers || isSaving}
                        className="w-full border border-background-secondary dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-xs font-bold transition-all focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="">{t('removeAssignment') || 'Görev atamayı kaldır'}</option>
                        {availableUsers.map((userItem) => (
                          <option key={userItem.id} value={userItem.id}>
                            {userItem.full_name || `${userItem.first_name} ${userItem.last_name}`}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>

                  {/* Reporter - Sadece görüntüleme, değiştirilemez */}
                  <div>
                    <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-3">{t('reporter')}</h4>
                    {ticket.created_by_user ? (
                      <div className="flex items-center space-x-3 p-2 bg-background-secondary/30 dark:bg-dark-background-secondary/10 rounded-lg border border-background-secondary dark:border-dark-border">
                        <div className="w-9 h-9 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm">
                          {ticket.created_by_user.first_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-text-primary dark:text-dark-text-primary truncate">
                            {ticket.created_by_user.full_name ||
                              `${ticket.created_by_user.first_name || ''} ${ticket.created_by_user.last_name || ''}`.trim() ||
                              t('unknownUser') || 'Unknown User'}
                          </p>
                          <p className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary truncate uppercase tracking-wider">
                            {ticket.created_by_user.email || 'No email'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-text-secondary dark:text-dark-text-secondary italic font-medium">{t('noReporterInfo') || 'Reporter bilgisi bulunmuyor'}</p>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="space-y-4 pt-4 border-t border-background-secondary dark:border-dark-border">
                    <div>
                      <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-2">{t('createdDate')}</h4>
                      <div className="flex items-center space-x-2 text-sm text-text-primary dark:text-dark-text-primary font-medium">
                        <CalendarDaysIcon className="w-4 h-4 text-text-secondary" />
                        <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US') : 'N/A'}</span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-2">{t('lastUpdate')}</h4>
                      <div className="flex items-center space-x-2 text-sm text-text-primary dark:text-dark-text-primary font-medium">
                        <ClockIcon className="w-4 h-4 text-text-secondary" />
                        <span>{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US') : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Time Tracking */}
                  <div>
                    <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-3">{t('timeTracking') || 'Time Tracking'}</h4>
                    <div className="space-y-3 p-3 bg-background-secondary/30 dark:bg-dark-background-secondary/10 rounded-lg border border-background-secondary dark:border-dark-border">
                      <div className="flex justify-between text-xs font-medium">
                        <span className="text-text-secondary dark:text-dark-text-secondary">{t('estimated') || 'Tahmini'}:</span>
                        <span className="text-text-primary dark:text-dark-text-primary">8h</span>
                      </div>
                      <div className="flex justify-between text-xs font-medium">
                        <span className="text-text-secondary dark:text-dark-text-secondary">{t('logged') || 'Kaydedilen'}:</span>
                        <span className="text-text-primary dark:text-dark-text-primary">5h 30m</span>
                      </div>
                      <div className="flex justify-between text-xs font-medium">
                        <span className="text-text-secondary dark:text-dark-text-secondary">{t('remaining') || 'Kalan'}:</span>
                        <span className="text-text-primary dark:text-dark-text-primary">2h 30m</span>
                      </div>
                      <div className="w-full bg-background-main dark:bg-dark-background-main rounded-full h-1.5 overflow-hidden">
                        <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: '70%', transition: 'width 1s ease-in-out' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Labels */}
                  <div>
                    <h4 className="font-bold text-[10px] uppercase tracking-widest text-text-secondary dark:text-dark-text-secondary mb-2">{t('labels') || 'Etiketler'}</h4>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-primary-100 dark:border-primary-800">authentication</span>
                      <span className="bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-success-100 dark:border-success-800">backend</span>
                      <span className="bg-highlight-50 text-highlight-700 dark:bg-highlight-900/20 dark:text-highlight-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-highlight-100 dark:border-highlight-800">security</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="p-6">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Add Comment Form */}
                <div className="bg-background-secondary/50 dark:bg-dark-background-secondary/30 rounded-2xl p-6 border border-background-secondary dark:border-dark-border shadow-sm">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                      {user?.first_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-text-primary dark:text-dark-text-primary">{user?.first_name ? `${user.first_name} ${user.last_name || ''}` : t('me') || 'Ben'}</p>
                      <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">{t('addingNewComment') || 'Yorum ekliyorsunuz'}</p>
                    </div>
                  </div>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('addCommentPlaceholder') || "Add a comment..."}
                    rows={4}
                    className="w-full border border-background-secondary dark:border-dark-border bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder:text-text-secondary/50"
                  />
                  {commentFiles.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {commentFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 px-3 py-1.5 rounded-lg text-xs font-bold border border-primary-200 dark:border-primary-800">
                          <span className="truncate max-w-[200px]">{file.name}</span>
                          <button onClick={() => removeCommentFile(index)} className="hover:text-error-500">
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between items-center mt-4">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 rounded-xl hover:bg-white dark:hover:bg-dark-background-secondary transition-colors"
                      title={t('attachFiles') || 'Dosya Ekle'}
                    >
                      <PaperClipIcon className="w-6 h-6" />
                    </button>
                    <button
                      onClick={handleAddComment}
                      disabled={(!newComment.trim() && commentFiles.length === 0) || addingComment}
                      className="px-8 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-bold uppercase tracking-widest transition-all shadow-lg shadow-primary-500/20 active:scale-95"
                    >
                      {addingComment ? (t('adding') || 'Adding...') : t('addComment')}
                    </button>
                  </div>
                </div>

                {/* Comments List */}
                <div className="space-y-6">
                  {loadingComments ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-sm text-text-secondary dark:text-dark-text-secondary font-medium">{t('loadingComments') || 'Yorumlar yükleniyor...'}</p>
                    </div>
                  ) : comments.length > 0 ? (
                    <div className="space-y-6">
                      {comments.map(comment => (
                        <div key={comment.id} className="flex space-x-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-bold ring-4 ring-white dark:ring-dark-background-main shadow-sm">
                              {comment.user?.first_name?.charAt(0) || comment.user?.email?.charAt(0) || '?'}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="bg-background-secondary/50 dark:bg-dark-background-secondary/50 border border-background-secondary dark:border-dark-border rounded-2xl p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-3 pb-2 border-b border-background-main/50 dark:border-dark-background-main/50">
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-text-primary dark:text-dark-text-primary">
                                    {comment.user?.first_name && comment.user?.last_name
                                      ? `${comment.user.first_name} ${comment.user.last_name}`
                                      : comment.user?.email || (t('anonymous') || 'Anonim')
                                    }
                                  </span>
                                  <span className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary uppercase tracking-widest mt-0.5">
                                    {comment.user?.role?.role_name || (t('user') || 'Kullanıcı')}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <span className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary uppercase tracking-widest bg-background-main dark:bg-dark-background-main px-2 py-1 rounded-md">
                                    {comment.created_at ? new Date(comment.created_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US') : 'N/A'}
                                  </span>
                                  {(comment.user?.id === user?.id || isAdmin) && (
                                    <div className="flex items-center space-x-1">
                                      <button
                                        onClick={() => handleEditComment(comment)}
                                        className="p-1.5 text-text-secondary hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-white dark:hover:bg-dark-background-main transition-all"
                                        title={t('edit')}
                                      >
                                        <PencilIcon className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="p-1.5 text-text-secondary hover:text-error-600 dark:hover:text-error-400 rounded-lg hover:bg-white dark:hover:bg-dark-background-main transition-all"
                                        title={t('delete')}
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {editingCommentId === comment.id ? (
                                <div className="space-y-3 mt-2">
                                  <textarea
                                    value={editedCommentText}
                                    onChange={(e) => setEditedCommentText(e.target.value)}
                                    rows={3}
                                    className="w-full border border-primary-500 dark:border-primary-400 bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                  />
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={handleCancelEditComment}
                                      className="px-4 py-1.5 text-xs font-bold text-text-secondary dark:text-dark-text-secondary hover:bg-background-main dark:hover:bg-dark-background-main rounded-lg transition-all"
                                    >
                                      {t('cancel')}
                                    </button>
                                    <button
                                      onClick={() => handleUpdateComment(comment.id)}
                                      disabled={updatingComment || !editedCommentText.trim()}
                                      className="px-5 py-1.5 text-xs font-bold bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-all shadow-md shadow-primary-500/10"
                                    >
                                      {updatingComment ? (t('updating') || 'Updating...') : (t('update') || 'Update')}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-text-primary dark:text-dark-text-primary whitespace-pre-wrap leading-relaxed">{comment.comment_text}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20 bg-background-secondary/20 dark:bg-dark-background-secondary/10 rounded-3xl border-2 border-dashed border-background-secondary dark:border-dark-border">
                      <ChatBubbleLeftIcon className="w-16 h-16 text-text-secondary dark:text-dark-text-secondary mx-auto mb-4 opacity-10" />
                      <p className="text-lg font-bold text-text-secondary dark:text-dark-text-secondary mb-2">{t('noComments') || 'Henüz yorum yok'}</p>
                      <p className="text-sm text-text-secondary/60 dark:text-dark-text-secondary/60">{t('startConversation') || 'Bir şeyler yazarak birleşimi başlatın.'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="p-6">
              {activityError && (
                <div className="mb-4 px-4 py-3 rounded border bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 text-sm flex justify-between items-start gap-2">
                  <span>{activityError}</span>
                  <button
                    type="button"
                    onClick={() => setActivityError(null)}
                    className="shrink-0 text-red-500 hover:text-red-700 dark:hover:text-red-200"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
              {activityLoading && !activityError && (
                <div className="mb-4 px-4 py-3 rounded border bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 text-sm">
                  {t('historyLoading')}
                </div>
              )}
              <div className="space-y-4">
                {!activityLoading && !activityError && activityItems.length === 0 && (
                  <p className="text-sm text-center text-text-secondary dark:text-dark-text-secondary py-8">
                    {t('historyEmpty')}
                  </p>
                )}
                {activityItems.map((row) => renderHistoryEntry(row))}
              </div>
            </div>
          )}

          {activeTab === 'subtickets' && ticket && projectIdForSubs && (
            <SubTicketsTab
              parentTicket={ticket}
              projectId={projectIdForSubs}
              onRefreshParent={handleRefreshParentTicket}
              onOpenChildTicket={onTicketChange ? handleOpenChildFromSubTab : undefined}
              t={t}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-background-secondary dark:border-dark-border px-6 py-4 bg-background-main dark:bg-dark-background-alt">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-bold text-text-secondary dark:text-dark-text-secondary uppercase tracking-widest">{t('status')}:</span>
                <select className="border border-background-secondary dark:border-dark-border bg-white dark:bg-dark-background-main text-text-primary dark:text-dark-text-primary rounded-lg px-3 py-1.5 text-xs font-bold transition-all focus:ring-2 focus:ring-primary-500/20">
                  <option>{t('pending') || 'To Do'}</option>
                  <option>{t('inProgress') || 'In Progress'}</option>
                  <option>{t('completed') || 'Done'}</option>
                </select>
              </div>
              <button className="text-xs font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 uppercase tracking-widest transition-all">
                {t('logWork') || 'Log Work'}
              </button>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-5 py-2 text-xs font-bold text-text-secondary dark:text-dark-text-secondary bg-white dark:bg-dark-background-secondary border border-background-secondary dark:border-dark-border rounded-lg hover:bg-background-secondary dark:hover:bg-dark-background-secondary/80 transition-all uppercase tracking-widest"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-bold uppercase tracking-widest transition-all shadow-md shadow-primary-500/20"
              >
                {isSaving ? (t('saving') || 'Saving...') : t('saveChanges')}
              </button>
            </div>
          </div>
        </div>
      </div>



      {/* Görsel Önizleme Modal'ı */}
      <ImagePreview
        images={images}
        currentIndex={currentImageIndex}
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        onNavigate={handleImageNavigate}
      />
    </div>,
    document.body
  );
};

export default TicketDetailModal; 